import jsPDF from 'jspdf';
// docx is dynamically imported inside downloadAsDocx() to keep it out of
// the synchronous parse path (avoids a known build-time babel-transform
// issue with docx@9.x super() pattern on production builds).
import { saveAs } from 'file-saver';

/**
 * Lazy-load the Roboto TTF (full Polish glyph coverage) and register it as
 * a custom jsPDF font. Cached across calls so the network fetch happens once
 * per session. Without this, jsPDF's default Helvetica renders Polish
 * diacritics (ą/ć/ę/ł/ń/ó/ś/ź/ż) as blank boxes.
 */
let robotoBase64Promise = null;

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadRobotoBase64 = () => {
  if (!robotoBase64Promise) {
    robotoBase64Promise = fetch('/fonts/Roboto-Regular.ttf')
      .then((res) => {
        if (!res.ok) throw new Error(`font fetch failed: ${res.status}`);
        return res.arrayBuffer();
      })
      .then(arrayBufferToBase64)
      .catch((err) => {
        // Reset cache on failure so a retry can attempt the fetch again.
        robotoBase64Promise = null;
        throw err;
      });
  }
  return robotoBase64Promise;
};

const registerRoboto = (doc, b64) => {
  doc.addFileToVFS('Roboto-Regular.ttf', b64);
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold'); // Same TTF — bold is faked via setFont weight, fine for action sheets
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

/**
 * Download text as TXT file
 */
export const downloadAsTxt = (text, filename = 'hussarai-result.txt') => {
  try {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, filename);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to download TXT:', error);
    }
    return false;
  }
};

/**
 * Download text as PDF
 * @param {string} text - Content to include in PDF
 * @param {string} filename - Output filename
 * @param {boolean} includeBranding - Whether to include HussarAi branding (default: true)
 */
export const downloadAsPdf = (text, filename = 'hussarai-result.pdf', includeBranding = true) => {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margins = 20;

    // Add HussarAi header (premium users can disable)
    if (includeBranding) {
      doc.setFontSize(16);
      doc.setTextColor(0, 212, 255);
      doc.text('HussarAi', margins, yPosition);
      
      // Add date
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      const date = new Date().toLocaleDateString('pl-PL');
      doc.text(date, margins, yPosition + 7);
      
      // Add divider line
      doc.setDrawColor(0, 212, 255);
      doc.line(margins, yPosition + 10, pageWidth - margins, yPosition + 10);
      
      yPosition = yPosition + 20;
    }
    
    // Add content
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    
    // Split text into lines that fit the page width
    const maxLineWidth = pageWidth - (margins * 2);
    const lines = doc.splitTextToSize(text, maxLineWidth);
    
    const lineHeight = 7;
    const pageHeight = doc.internal.pageSize.getHeight();
    const bottomMargin = includeBranding ? 20 : 15;
    
    lines.forEach((line) => {
      if (yPosition + lineHeight > pageHeight - bottomMargin) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(line, margins, yPosition);
      yPosition += lineHeight;
    });
    
    // Add footer (premium users can disable)
    if (includeBranding) {
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Strona ${i} z ${totalPages} | hussarai.pl`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
    }
    
    doc.save(filename);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to download PDF:', error);
    }
    return false;
  }
};

/**
 * Download text as DOCX (Word document)
 * @param {string} text - Content to include in document
 * @param {string} filename - Output filename
 * @param {boolean} includeBranding - Whether to include HussarAi branding (default: true)
 */
export const downloadAsDocx = async (text, filename = 'hussarai-result.docx', includeBranding = true) => {
  try {
    // Lazy-load docx so the production build never has to parse its
    // arrow-super() pattern in the main bundle.
    const { Document, Packer, Paragraph, TextRun } = await import('docx');
    // Split text into paragraphs
    const paragraphs = text.split('\n').filter(p => p.trim() !== '');
    
    const children = [];
    
    // Add header (premium users can disable)
    if (includeBranding) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: 'HussarAi',
              bold: true,
              size: 32,
              color: '00D4FF'
            })
          ],
          spacing: {
            after: 200
          }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: new Date().toLocaleDateString('pl-PL', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }),
              size: 20,
              color: '808080'
            })
          ],
          spacing: {
            after: 400
          }
        })
      );
    }
    
    // Add content paragraphs
    children.push(
      ...paragraphs.map(para => 
        new Paragraph({
          children: [
            new TextRun({
              text: para,
              size: 22
            })
          ],
          spacing: {
            after: 200
          }
        })
      )
    );
    
    const doc = new Document({
      sections: [{
        properties: {},
        children: children
      }]
    });
    
    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to download DOCX:', error);
    }
    return false;
  }
};

/**
 * Generate filename from text content
 */
export const generateFilename = (text, extension, mode = 'general') => {
  // Take first 30 chars and clean
  const title = text
    .substring(0, 30)
    .replace(/[^a-zA-Z0-9ąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();
  
  const timestamp = new Date().toISOString().split('T')[0];
  const modePrefix = mode !== 'general' ? `${mode}-` : '';
  
  return `hussarai-${modePrefix}${title}-${timestamp}.${extension}`;
};

/**
 * Download a Lead Desk output as a clean HussarAi "action sheet" PDF.
 *
 * Designed as a forwardable artifact a small-business owner can hand to
 * staff, paste into a CRM, or share with a client manager. Restrained
 * branding, action-sheet layout (NOT a decorative report).
 *
 * Structure (per pivot spec May 31 2026):
 *   1. Lead         — short digest
 *   2. Assessment   — urgency + value signals
 *   3. Reply Draft  — ready-to-send message
 *   4. Missing Information — checklist with [ ] boxes
 *   5. Next Step    — recommended action + follow-up timing
 *
 * @param {object} lead - Normalised Lead Desk payload from /api/operator/lead/process
 * @param {object} options - { filename, language, labels }
 *   labels: optional translated headings { lead, assessment, replyDraft,
 *           missingInfo, nextStep, urgency, followUp, actionPrefix }
 */
export const downloadLeadActionSheet = async (lead, options = {}) => {
  if (!lead) return false;

  const { filename = 'hussarai-lead-action-sheet.pdf', language = 'pl' } = options;
  const labels = options.labels || {};

  const FALLBACK = language === 'en'
    ? {
        documentTitle: 'Lead Action Sheet',
        lead: 'Lead',
        assessment: 'Assessment',
        replyDraft: 'Reply Draft',
        missingInfo: 'Missing Information',
        nextStep: 'Next Step',
        urgency: 'Urgency',
        followUp: 'Follow-up',
        followUpToday: 'today',
        followUpDays: (d) => `in ${d} day${d === 1 ? '' : 's'}`,
        confidence: 'Confidence',
        urgencyMap: { high: 'High priority', medium: 'Medium priority', low: 'Low priority' },
        actionMap: {
          reply_now: 'Reply now',
          send_quote: 'Send a quote',
          ask_questions: 'Ask for missing information',
          schedule_callback: 'Schedule a callback',
          follow_up_later: 'Follow up later',
          disqualify: 'Disqualify — poor fit',
        },
        footer: 'HussarAi Lead Operator · hussarai.pl',
        generated: 'Generated',
      }
    : {
        documentTitle: 'Lead Action Sheet',
        lead: 'Lead',
        assessment: 'Ocena',
        replyDraft: 'Gotowa odpowiedź',
        missingInfo: 'Brakujące informacje',
        nextStep: 'Następny krok',
        urgency: 'Pilność',
        followUp: 'Follow-up',
        followUpToday: 'dzisiaj',
        followUpDays: (d) => `za ${d} dni`,
        confidence: 'Pewność',
        urgencyMap: { high: 'Wysoki priorytet', medium: 'Średni priorytet', low: 'Niski priorytet' },
        actionMap: {
          reply_now: 'Odpisz teraz',
          send_quote: 'Wyślij ofertę',
          ask_questions: 'Dopytaj brakujące informacje',
          schedule_callback: 'Umów rozmowę',
          follow_up_later: 'Follow-up później',
          disqualify: 'Odznacz — niskie dopasowanie',
        },
        footer: 'HussarAi Lead Operator · hussarai.pl',
        generated: 'Wygenerowano',
      };

  const L = { ...FALLBACK, ...labels };

  try {
    const robotoB64 = await loadRobotoBase64();
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    registerRoboto(doc, robotoB64);
    const FONT = 'Roboto';
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const contentWidth = pageWidth - margin * 2;

    let y = margin;

    // -------- Helpers --------
    const ensureSpace = (needed) => {
      if (y + needed > pageHeight - 22) {
        doc.addPage();
        y = margin;
      }
    };

    const sectionHeader = (label) => {
      ensureSpace(14);
      doc.setFont(FONT, 'bold');
      doc.setFontSize(8);
      doc.setTextColor(0, 168, 204);
      doc.text(label.toUpperCase(), margin, y);
      doc.setDrawColor(0, 168, 204);
      doc.setLineWidth(0.3);
      doc.line(margin, y + 1.6, margin + 24, y + 1.6);
      y += 7;
      doc.setTextColor(0, 0, 0);
    };

    const bodyText = (text, opts = {}) => {
      const { size = 10.5, bold = false, color = [25, 25, 25] } = opts;
      doc.setFont(FONT, bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text || '', contentWidth);
      const lh = size * 0.45 + 1.5;
      lines.forEach((line) => {
        ensureSpace(lh + 2);
        doc.text(line, margin, y);
        y += lh;
      });
    };

    // -------- Header --------
    doc.setFont(FONT, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 168, 204);
    doc.text('HussarAi', margin, y);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    doc.text(L.documentTitle.toUpperCase(), margin + 28, y);

    const dateString = new Date().toLocaleDateString(language === 'en' ? 'en-GB' : 'pl-PL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    doc.text(`${L.generated}: ${dateString}`, pageWidth - margin, y, { align: 'right' });

    y += 4;
    doc.setDrawColor(0, 168, 204);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageWidth - margin, y);
    y += 9;

    // -------- 1. Lead --------
    sectionHeader(L.lead);
    bodyText(lead.lead_summary || '—', { size: 12, bold: true });
    y += 3;

    // -------- 2. Assessment --------
    sectionHeader(L.assessment);
    const urgencyLabel = L.urgencyMap[lead.urgency] || lead.urgency || '—';
    bodyText(`${L.urgency}: ${urgencyLabel}`, { size: 10.5, bold: true });
    if (typeof lead.confidence === 'number') {
      bodyText(`${L.confidence}: ${Math.round(lead.confidence * 100)}%`, {
        size: 9.5,
        color: [110, 110, 110],
      });
    }
    if (Array.isArray(lead.value_signals) && lead.value_signals.length > 0) {
      y += 2;
      lead.value_signals.forEach((sig) => {
        bodyText(`•  ${sig}`, { size: 10 });
      });
    }
    y += 3;

    // -------- 3. Reply Draft --------
    sectionHeader(L.replyDraft);
    // Draw left rule for the reply block
    const replyStartY = y;
    bodyText(lead.ready_reply || '—', { size: 10.5 });
    doc.setDrawColor(0, 168, 204);
    doc.setLineWidth(0.8);
    doc.line(margin - 4, replyStartY - 4, margin - 4, y - 1);
    y += 3;

    // -------- 4. Missing Information --------
    if (Array.isArray(lead.missing_info) && lead.missing_info.length > 0) {
      sectionHeader(L.missingInfo);
      lead.missing_info.forEach((item) => {
        ensureSpace(8);
        // Empty checkbox
        doc.setDrawColor(80, 80, 80);
        doc.setLineWidth(0.3);
        doc.rect(margin, y - 3.2, 3.2, 3.2);
        const lines = doc.splitTextToSize(item, contentWidth - 7);
        doc.setFont(FONT, 'normal');
        doc.setFontSize(10.5);
        doc.setTextColor(25, 25, 25);
        lines.forEach((line, idx) => {
          if (idx > 0) ensureSpace(5.5);
          doc.text(line, margin + 6, y);
          y += 5.5;
        });
        y += 0.5;
      });
      y += 2;
    }

    // -------- 5. Next Step --------
    sectionHeader(L.nextStep);
    const actionLabel = L.actionMap[lead.recommended_action] || lead.recommended_action || '—';
    bodyText(actionLabel, { size: 12, bold: true });
    const followUpText =
      lead.follow_up_in_days === 0
        ? L.followUpToday
        : L.followUpDays(lead.follow_up_in_days || 0);
    bodyText(`${L.followUp}: ${followUpText}`, { size: 10.5, color: [80, 80, 80] });

    // -------- Footer --------
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i += 1) {
      doc.setPage(i);
      doc.setFont(FONT, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text(L.footer, margin, pageHeight - 10);
      doc.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }

    doc.save(filename);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to generate Lead action sheet PDF:', error);
    }
    return false;
  }
};

